import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"
import {
  getMoneyReceipts,
  getMoneyReceipt,
  getMoneyReceiptBlueprint,
  getMoneyReceiptLoan,
  createMoneyReceipt,
  editMoneyReceipt,
  deleteMoneyReceipt,
  createMoneyReceiptFromQuotation,
  cancelMoneyReceipt,
  saveMoneyReceiptLoan,
  deleteMoneyReceiptLoan,
  getMoneyReceiptsByCustomer,
  getMoneyReceiptStatistics,
} from "api/moneyReceiptApi"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useToastMessage } from "./useToastMessage"

export const MONEY_RECEIPT_QUERY_KEY = "moneyReceipts"

// -------------------- FETCH PAGINATED --------------------
export const useMoneyReceipts = (params = {}, enabled = true) => {
  return usePaginatedResource(
    MONEY_RECEIPT_QUERY_KEY,
    getMoneyReceipts,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useMoneyReceipt = (id, enabled = true) => {
  return useResourceItem(MONEY_RECEIPT_QUERY_KEY, getMoneyReceipt, id, enabled)
}

export const useMoneyReceiptBlueprint = (id, enabled = true) => {
  return useQuery({
    queryKey: [MONEY_RECEIPT_QUERY_KEY, "blueprint", id],
    queryFn: () => getMoneyReceiptBlueprint(id),
    enabled: !!id && enabled,
    staleTime: 0,
  })
}

export const useMoneyReceiptLoan = (id, enabled = true) => {
  return useResourceItem(
    [MONEY_RECEIPT_QUERY_KEY, "loan"],
    getMoneyReceiptLoan,
    id,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateMoneyReceipt = () => {
  return useCreateResource(createMoneyReceipt, MONEY_RECEIPT_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditMoneyReceipt = () => {
  return useEditResource(editMoneyReceipt, MONEY_RECEIPT_QUERY_KEY)
}

// -------------------- DELETE --------------------
export const useDeleteMoneyReceipt = () => {
  return useDeleteResource(deleteMoneyReceipt, MONEY_RECEIPT_QUERY_KEY)
}

// -------------------- CREATE FROM QUOTATION --------------------
export const useCreateMoneyReceiptFromQuotation = () => {
  const queryClient = useQueryClient()
  const { handleResponse, handleError } = useToastMessage()

  return useMutation({
    mutationFn: createMoneyReceiptFromQuotation,
    onSuccess: data => {
      queryClient.invalidateQueries([MONEY_RECEIPT_QUERY_KEY])
      handleResponse(
        data,
        "Money receipt created from quotation successfully",
        true
      )
      return data
    },
    onError: error => {
      handleError(error, "Failed to create money receipt from quotation")
    },
  })
}

// -------------------- CANCEL --------------------
export const useCancelMoneyReceipt = () => {
  const queryClient = useQueryClient()
  const { handleResponse, handleError } = useToastMessage()

  return useMutation({
    mutationFn: cancelMoneyReceipt,
    onSuccess: data => {
      queryClient.invalidateQueries([MONEY_RECEIPT_QUERY_KEY])
      handleResponse(data, "Money receipt cancelled successfully", true)
      return data
    },
    onError: error => {
      handleError(error, "Failed to cancel money receipt")
    },
  })
}

export const useSaveMoneyReceiptLoan = () => {
  const queryClient = useQueryClient()
  const { handleResponse, handleError } = useToastMessage()

  return useMutation({
    mutationFn: saveMoneyReceiptLoan,
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [MONEY_RECEIPT_QUERY_KEY],
        refetchType: "active",
      })
      handleResponse(
        data,
        "Money receipt loan information saved successfully",
        true
      )
      return data
    },
    onError: error => {
      handleError(error, "Failed to save money receipt loan information")
    },
  })
}

export const useDeleteMoneyReceiptLoan = () => {
  const queryClient = useQueryClient()
  const { handleResponse, handleError } = useToastMessage()

  return useMutation({
    mutationFn: deleteMoneyReceiptLoan,
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: [MONEY_RECEIPT_QUERY_KEY],
        refetchType: "active",
      })
      handleResponse(
        data,
        "Money receipt loan information deleted successfully",
        true
      )
      return data
    },
    onError: error => {
      handleError(error, "Failed to delete money receipt loan information")
    },
  })
}

// -------------------- CUSTOMER MONEY RECEIPTS --------------------
export const useMoneyReceiptsByCustomer = (
  customerId,
  params = {},
  enabled = true
) => {
  return usePaginatedResource(
    [MONEY_RECEIPT_QUERY_KEY, "customer", customerId],
    queryParams => getMoneyReceiptsByCustomer(customerId, queryParams),
    params,
    enabled && !!customerId
  )
}

// -------------------- STATISTICS --------------------
export const useMoneyReceiptStatistics = (enabled = true) => {
  return useResourceItem(
    [MONEY_RECEIPT_QUERY_KEY, "statistics"],
    getMoneyReceiptStatistics,
    "statistics",
    enabled
  )
}

// -------------------- UTILITY HOOKS --------------------

// Calculate payment status
export const usePaymentStatus = (totalAmount, amountPaid) => {
  if (amountPaid === 0) return "pending"
  if (amountPaid < totalAmount) return "partially_paid"
  return "fully_paid"
}

// Format money receipt number for display
export const useFormatMRNumber = () => {
  return mrNumber => {
    if (!mrNumber) return ""
    // Format MR260401001 as MR-26-04-01001
    if (mrNumber.length >= 10) {
      return `${mrNumber.substr(0, 2)}-${mrNumber.substr(
        2,
        2
      )}-${mrNumber.substr(4, 2)}-${mrNumber.substr(6)}`
    }
    return mrNumber
  }
}

// Get payment status color
export const usePaymentStatusColor = () => {
  return status => {
    switch (status) {
      case "pending":
        return "warning"
      case "partially_paid":
        return "info"
      case "fully_paid":
        return "success"
      default:
        return "secondary"
    }
  }
}

// Get payment status label
export const usePaymentStatusLabel = () => {
  return status => {
    switch (status) {
      case "pending":
        return "Pending"
      case "partially_paid":
        return "Partially Paid"
      case "fully_paid":
        return "Fully Paid"
      default:
        return "Unknown"
    }
  }
}

// Format currency for display
export const useFormatCurrency = () => {
  return amount => {
    if (typeof amount !== "number") return "0"
    return new Intl.NumberFormat("en-BD", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }
}
